
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAppStore } from "@/lib/store";
import { categories } from "@/lib/data/categories";
import { CategorySlug } from "@/lib/types";
import ProductCard from "@/components/product/ProductCard";
import RevealOnScroll from "@/components/ui/RevealOnScroll";
import PageHero from "@/components/ui/PageHero";
import { useDocumentMeta } from "@/lib/useDocumentMeta";
import { cn } from "@/lib/utils";

export default function MarketplacePage() {
  useDocumentMeta({
    title: "Marketplace",
    description:
      "Browse second-hand Marvel, anime, gaming, Pokémon, Star Wars, LEGO, comics and collector finds from sellers across the Nerd Multiverse.",
    path: "/marketplace",
  });

  const [searchParams] = useSearchParams();
  const initialCategory = searchParams.get("category") as CategorySlug | null;
  const rail = searchParams.get("rail");

  const products = useAppStore((s) => s.products);
  const [activeCategory, setActiveCategory] = useState<CategorySlug | "all">(
    initialCategory ?? "all"
  );
  const [query, setQuery] = useState(searchParams.get("query") ?? "");
  const [sort, setSort] = useState<"newest" | "price-asc" | "price-desc">(
    "newest"
  );

  const filtered = useMemo(() => {
    let list = products.filter((p) => p.status === "approved");
    if (activeCategory !== "all") {
      list = list.filter((p) => p.category === activeCategory);
    }
    if (rail === "rare-finds") list = list.filter((p) => p.isRareFind);
    if (rail === "new") list = [...list].sort((a, b) => b.createdAt - a.createdAt);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.description.toLowerCase().includes(q)
      );
    }
    if (sort === "price-asc") list = [...list].sort((a, b) => a.price - b.price);
    if (sort === "price-desc") list = [...list].sort((a, b) => b.price - a.price);
    if (sort === "newest") list = [...list].sort((a, b) => b.createdAt - a.createdAt);
    return list;
  }, [products, activeCategory, rail, query, sort]);

  return (
    <div className="pb-24">
      <PageHero
        desktop="/images/hero/marketplace-desktop.webp"
        mobile="/images/hero/marketplace-mobile-v2.webp"
        alt="A masked cosmic explorer leaping through a radiant floating-island collectors market"
      >
        <RevealOnScroll>
          <span className="nm-kicker">Marketplace</span>
          <h1 className="mt-5 font-display text-4xl md:text-6xl font-black tracking-[-.045em] text-white">
            Browse the Multiverse
          </h1>
        </RevealOnScroll>
      </PageHero>

      <div className="nm-container">
        <div className="mt-10 flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
          <div className="flex gap-2 overflow-x-auto nm-scrollbar-none pb-1">
            <button
              onClick={() => setActiveCategory("all")}
              className={cn(
                "shrink-0 px-4 py-2 rounded-full text-sm border transition-colors",
                activeCategory === "all"
                  ? "bg-white text-[#05070d] border-white"
                  : "border-white/15 text-[var(--nm-ink-muted)] hover:border-white/40"
              )}
            >
              All
            </button>
            {categories.map((c) => (
              <button
                key={c.slug}
                onClick={() => setActiveCategory(c.slug)}
                className={cn(
                  "shrink-0 px-4 py-2 rounded-full text-sm border transition-colors",
                  activeCategory === c.slug
                    ? "bg-white text-[#05070d] border-white"
                    : "border-white/15 text-[var(--nm-ink-muted)] hover:border-white/40"
                )}
              >
                {c.name}
              </button>
            ))}
          </div>

          <div className="flex gap-3">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search the multiverse…"
              className="w-full lg:w-64 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-white placeholder:text-[var(--nm-ink-muted)] focus:outline-none focus:border-[var(--nm-blue-bright)] transition-colors"
            />
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as typeof sort)}
              aria-label="Sort listings"
              className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-white focus:outline-none focus:border-[var(--nm-blue-bright)] transition-colors"
            >
              <option value="newest">Newest</option>
              <option value="price-asc">Price: Low to High</option>
              <option value="price-desc">Price: High to Low</option>
            </select>
          </div>
        </div>

        {filtered.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-24 text-center"
          >
            <p className="font-display text-2xl text-white">
              No treasures here yet.
            </p>
            <p className="mt-2 text-[var(--nm-ink-muted)]">
              Your next find could be waiting somewhere else in the Multiverse.
            </p>
          </motion.div>
        ) : (
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {filtered.map((p, i) => (
              <RevealOnScroll key={p.id} delay={(i % 8) * 0.04}>
                <ProductCard product={p} />
              </RevealOnScroll>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
